import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import Backdrop from './Backdrop';

// Styled components for the modal
const ModalWrapper = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: ${({ width }) => width || '90%'};
  max-width: 600px;
  background: ${({ contentBgColor }) => contentBgColor || '#fff'};
  border-radius: 8px;
  z-index: 1001;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  padding: 16px;
  background: ${({ headerBgColor }) => headerBgColor || '#f0f0f0'};
  color: ${({ headerColor }) => headerColor || '#333'};
  text-align: ${({ headerTextAlign }) => headerTextAlign || 'left'};
  border-bottom: 1px solid #e0e0e0;
`;

const ModalContent = styled.div`
  padding: 8px 16px;
`;

const ModalFooter = styled.footer`
  padding: 16px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const ModalOverlay = ({ header, children, footer, width, contentBgColor, headerTextAlign, headerBgColor, headerColor, footerClass }) => {
  const content = (
    <ModalWrapper width={width} contentBgColor={contentBgColor}>
      {header && (
        <ModalHeader headerTextAlign={headerTextAlign} headerBgColor={headerBgColor} headerColor={headerColor}>
          <h2>{header}</h2>
        </ModalHeader>
      )}
      <ModalContent>{children}</ModalContent>
      {footer && <ModalFooter className={footerClass}>{footer}</ModalFooter>}
    </ModalWrapper>
  );
  return createPortal(content, document.getElementById('modal'));
};

const Modal = ({ show, onClose, header, children, footer, width, contentBgColor, headerTextAlign, headerBgColor, headerColor, footerClass }) => {
  // Close modal on Escape key press
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    if (show) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [show, onClose]);

  return (
    <>
      {show && <Backdrop onClick={onClose} />}
      <AnimatePresence>
        {show && (
          <motion.div
            initial={{ opacity: 0, y: -100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -100 }}
            transition={{ duration: 0.3 }}
          >
            <ModalOverlay
              header={header}
              footer={footer}
              width={width}
              contentBgColor={contentBgColor}
              headerTextAlign={headerTextAlign}
              headerBgColor={headerBgColor}
              headerColor={headerColor}
             
            >
              {children}
            </ModalOverlay>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

Modal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  header: PropTypes.string,
  children: PropTypes.node,
  footer: PropTypes.node,
  width: PropTypes.string,
  contentBgColor: PropTypes.string,
  headerTextAlign: PropTypes.string,
  headerBgColor: PropTypes.string,
  headerColor: PropTypes.string,
 
};

export default Modal;